import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { UtilsService } from '@utils/utils.service';
import {
  Response,
  LanguageResponse,
  QueryRequest,
  LocalStorageKeys,
  UserLanguageRequest,
  UserData,
  Language,
  LoginResponse,
  UserSession,
} from '@models/index.model';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  private UrlAPI = environment.apiUrl;
  private _defaultSupportedLanguages: LanguageResponse[] = [
    {
      language: Language.CATALAN,
      description: 'Català',
      default: false,
    },
    {
      language: Language.ENGLISH,
      description: 'English',
      default: true,
    },
    {
      language: Language.SPANISH,
      description: 'Español',
      default: false,
    },
    {
      language: Language.PORTUGUESE,
      description: 'Português',
      default: false,
    },
  ];

  constructor(private http: HttpClient, private _utils: UtilsService, private _translate: TranslateService) {}

  public getAvailableLanguages(): Observable<Response<Array<LanguageResponse>>> {
    const query = this.UrlAPI + QueryRequest.languages;
    return this.http.get<Response<Array<LanguageResponse>>>(query);
  }

  public getUserLanguage(): Observable<Response<LanguageResponse>> {
    const query = this.UrlAPI + QueryRequest.userLanguage;
    return this.http.get<Response<LanguageResponse>>(query);
  }

  public setUserLanguage(languageRequest: UserLanguageRequest): Observable<Response<any>> {
    const query = this.UrlAPI + QueryRequest.userLanguage;
    return this.http.put<Response<any>>(query, JSON.stringify(languageRequest));
  }

  public getBrowserLanguage(): string {
    return this.normalizeLang(navigator.language);
  }

  public getDefaultSupportedLanguages() {
    return this._defaultSupportedLanguages;
  }

  public getDefaultUserLanguage(): Language {
    const defaultSupportedLangs: Language[] = this._defaultSupportedLanguages.map(
      (lang: LanguageResponse) => lang.language
    );
    const userData: UserData = (this._utils.getLocalStorage(LocalStorageKeys.SESSION_KEY) as UserSession)?.userData;
    if (!!userData && !!userData.language && (defaultSupportedLangs as string[]).includes(userData.language)) {
      return userData.language as Language;
    }

    const browserLang: string = this.getBrowserLanguage();
    if (!!browserLang && (defaultSupportedLangs as string[]).includes(browserLang)) {
      return browserLang as Language;
    }

    return Language.ENGLISH;
  }

  public configureApplicationLanguage() {
    const defaultSupportedLangs: Language[] = this._defaultSupportedLanguages.map(
      (lang: LanguageResponse) => lang.language
    );
    const defaultUserLang: Language = this.getDefaultUserLanguage();

    this._translate.addLangs(defaultSupportedLangs);
    this._translate.setDefaultLang(defaultUserLang);
    return this._translate.use(defaultUserLang);
  }

  public normalizeLang(languageName: string): string {
    return languageName?.slice(0, 2).toLowerCase();
  }
}
